import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, memo, useEffect, useState } from 'react';
import { CloseIcon, PlusIcon, SearchIcon } from '~/assets/icons';
import { formatCurrency } from '~/utils/helper';
import { useTranslation } from 'react-i18next';
import { IPriceItemList } from '~/utils/interface/priceItem';
import EditPriceItemDialog from '~/components/price-items/EditPriceItem/EditPriceItemDialog';
import { DIALOG_MODE_CREATE } from '~/utils/constants/common';
import { PERMISSIONS } from '~/utils/constants/permission';
import usePermission from '~/hooks/usePermission';
import useDebounce from '~/hooks/useDebounce';

type AddPriceItemDialogProps = {
  open: boolean;
  setOpen: (open: boolean, needRefreshData?: boolean) => void;
  selectedPriceItems: (priceItems: IPriceItemList[]) => void;
  priceListItems: Array<IPriceItemList>;
  form: any;
  onSearchChange?: (text: string) => void;
};

const AddPriceItemDialog = (props: AddPriceItemDialogProps) => {
  const [selectedPriceItems, setSelectedPriceItems] = useState<IPriceItemList[]>([]);
  const [searchInput, setSearchInput] = useState<string>('');
  const [filteredPriceListItems, setFilteredPriceListItems] = useState<Array<IPriceItemList>>([]);
  const { t } = useTranslation('admin/job');
  const permissions = usePermission();
  const [openCreatePriceItemDialog, setOpenCreatePriceItemDialog] = useState<boolean>(false);
  const searchInputDebounce = useDebounce(searchInput);
  useEffect(() => {
    setFilteredPriceListItems(props.priceListItems.slice(0, 30));
  }, [props.priceListItems]);

  useEffect(() => {
    setSelectedPriceItems([]);
  }, [props.open]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const handleSetOpenCreatePriceItemDialog = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenCreatePriceItemDialog(open);
  };
  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, item: IPriceItemList) => {
    const { value, checked } = event.target;
    if (checked) {
      setSelectedPriceItems([...selectedPriceItems, item]);
    } else {
      const newSelectedItems = selectedPriceItems.filter((item) => item.id !== value);
      setSelectedPriceItems(newSelectedItems);
    }
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) setSelectedPriceItems(filteredPriceListItems);
    else setSelectedPriceItems([]);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchInput(e.target.value);
  };
  const handleOpenCreate = () => {
    setOpenCreatePriceItemDialog(true);
  };
  const handleCancel = () => {
    handleSetOpen(false);
  };

  useEffect(() => {
    if (props.onSearchChange) {
      props.onSearchChange(searchInputDebounce);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInputDebounce]);

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {t('estimate.priceListItem.title')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box className='flex justify-between items-center gap-[10px]'>
          <Box className='w-1/3'>
            <TextField
              value={searchInput}
              onChange={(e) => handleSearchChange(e)}
              placeholder={t('searchPlaceholder')}
              InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
            />
          </Box>

          <Box className='flex items-center gap-[10px]'>
            <Button
              className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px]'
              variant='contained'
              disableElevation
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={handleOpenCreate}
            >
              {t('addNew')}
            </Button>
            <FormControlLabel
              control={
                <Checkbox className='w-[20px] h-[20px]' color='primary' onChange={(event) => handleSelectAll(event)} />
              }
              label={t('selectAll', { ns: 'admin/common' })}
              value={true}
            />
          </Box>
        </Box>

        <Box className='flex flex-wrap'>
          {filteredPriceListItems.map((item, index) => (
            <Box className='relative w-1/2' key={index}>
              <IconButton className='absolute right-0 top-0'>
                <Checkbox
                  className='w-[20px] h-[20px]'
                  color='primary'
                  value={item.id}
                  onChange={(event) => handleCheckboxChange(event, item)}
                  checked={selectedPriceItems.some((selectedItem) => item.id === selectedItem.id)}
                />
              </IconButton>
              <Box className='block border-2 px-[50px] flex-1 m-1'>
                <Box className='flex mt-3 w-full'>
                  <InputLabel className='w-[100px] content-center	'> {t('estimate.priceListItem.name')}</InputLabel>
                  <Typography>{item.title}</Typography>
                </Box>
                <Box className='flex mt-3 w-full'>
                  <InputLabel className='w-[100px] content-center'> {t('estimate.priceListItem.code')}</InputLabel>
                  <Typography>{item.code}</Typography>
                </Box>
                <Box className='flex mt-3 w-full'>
                  <InputLabel className='w-[100px] content-center'> {t('estimate.priceListItem.unitPrice')}</InputLabel>
                  <Typography>{formatCurrency(item.unitPrice)}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions className='justify-end'>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            props.selectedPriceItems(selectedPriceItems);
            props.setOpen(false);
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
        {permissions[PERMISSIONS.priceItem_create] && (
          <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
            <EditPriceItemDialog
              open={openCreatePriceItemDialog}
              setOpen={handleSetOpenCreatePriceItemDialog}
              mode={DIALOG_MODE_CREATE}
              showFullField={false}
            />
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddPriceItemDialog);
